import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes } from "react-icons/fa";

// ======= ÍNDICE DE BÚSQUEDA =======
const indice = [
  // Páginas principales
  { titulo: "Inicio", descripcion: "Página principal de QuarkIA", ruta: "/" },
  { titulo: "Sobre nosotros", descripcion: "Misión, visión y equipo de QuarkIA", ruta: "/sobrenosotros" },
  { titulo: "Contáctenos", descripcion: "Formulario de contacto y datos", ruta: "/contactenos" },
  { titulo: "Registro", descripcion: "Crear cuenta en QuarkIA", ruta: "/register" },
  { titulo: "Iniciar sesión", descripcion: "Acceder a la plataforma", ruta: "/login" },
  { titulo: "Información gratuita", descripcion: "Dashboard público del sector energético", ruta: "/informaciongratuita" },
  { titulo: "Noticias", descripcion: "Energía al día – actualidad del sector", ruta: "/noticias" },
  { titulo: "Portafolio", descripcion: "Servicios y soluciones de QuarkIA", ruta: "/nuestroportafolio" },
  { titulo: "Servicios", descripcion: "Catálogo de servicios especializados", ruta: "/servicios" },

  // Servicios específicos (llevan a portafolio)
  { titulo: "Plataformas de información energética", descripcion: "Integración y visualización de datos del sector", ruta: "/nuestroportafolio" },
  { titulo: "Proyecciones de variables energéticas", descripcion: "Modelos predictivos e inteligencia artificial", ruta: "/nuestroportafolio" },
  { titulo: "Modelos de negocio basados en datos", descripcion: "Transformación de datos en oportunidades", ruta: "/nuestroportafolio" },
  { titulo: "Consultorías especializadas", descripcion: "Asesoría experta en sistemas eléctricos", ruta: "/nuestroportafolio" },
  { titulo: "Automatización de procesos", descripcion: "Reducción de costos y tareas repetitivas", ruta: "/nuestroportafolio" },
  { titulo: "Inteligencia artificial aplicada", descripcion: "Algoritmos de IA para el negocio energético", ruta: "/nuestroportafolio" },

  // Aliados
  { titulo: "Fractal BIC", descripcion: "Aliado estratégico – consultoría energética", ruta: "/" },
  { titulo: "CAC", descripcion: "Aliado estratégico – Comité Asesor de Comercialización", ruta: "/" },
  { titulo: "SinergIA", descripcion: "Aliado estratégico – innovación digital", ruta: "/" },
];

export default function SearchBar() {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [seleccionado, setSeleccionado] = useState(-1);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Filtrar resultados
  useEffect(() => {
    if (query.trim().length < 2) {
      setResultados([]);
      setAbierto(false);
      return;
    }
    const q = query.toLowerCase();
    const filtrados = indice.filter(
      (item) =>
        item.titulo.toLowerCase().includes(q) ||
        item.descripcion.toLowerCase().includes(q)
    );
    setResultados(filtrados.slice(0, 6));
    setAbierto(true);
    setSeleccionado(-1);
  }, [query]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setAbierto(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const navegar = (ruta) => {
    navigate(ruta);
    setQuery("");
    setAbierto(false);
    setResultados([]);
  };

  const limpiar = () => {
    setQuery("");
    setResultados([]);
    setAbierto(false);
    inputRef.current?.focus();
  };

  // Navegación con teclado
  const manejarTecla = (e) => {
    if (!abierto) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSeleccionado((prev) => Math.min(prev + 1, resultados.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSeleccionado((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && seleccionado >= 0) {
      navegar(resultados[seleccionado].ruta);
    } else if (e.key === "Escape") {
      setAbierto(false);
    }
  };

  // Resaltar coincidencia en el texto
  const resaltar = (texto) => {
    const idx = texto.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return texto;
    return (
      <>
        {texto.slice(0, idx)}
        <span className="text-[#00e68a] font-bold">
          {texto.slice(idx, idx + query.length)}
        </span>
        {texto.slice(idx + query.length)}
      </>
    );
  };

  return (
    <div ref={wrapperRef} className="relative">
      {/* Input */}
      <div className="flex items-center bg-[#2c2833] border border-white/10 rounded-full px-4 py-1.5 gap-2 focus-within:border-[#00e68a]/50 transition-all w-48 md:w-64">
        <FaSearch className="text-gray-400 text-sm flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={manejarTecla}
          placeholder="Buscar..."
          className="bg-transparent text-white text-sm outline-none w-full placeholder-gray-400"
        />
        {query && (
          <button onClick={limpiar} className="text-gray-400 hover:text-white transition">
            <FaTimes className="text-xs" />
          </button>
        )}
      </div>

      {/* Dropdown de resultados */}
      {abierto && (
        <div className="absolute top-full mt-2 right-0 w-80 bg-[#231f2b] border border-[#00e68a]/20 rounded-2xl shadow-2xl overflow-hidden z-50">
          {resultados.length > 0 ? (
            <>
              <p className="text-xs text-gray-500 px-4 pt-3 pb-1 uppercase tracking-wider">
                {resultados.length} resultado{resultados.length !== 1 ? "s" : ""}
              </p>
              <ul>
                {resultados.map((item, i) => (
                  <li
                    key={i}
                    onClick={() => navegar(item.ruta)}
                    className={`px-4 py-3 cursor-pointer transition-all border-b border-white/5 last:border-0 ${
                      seleccionado === i
                        ? "bg-[#00e68a]/10"
                        : "hover:bg-[#2c2833]"
                    }`}
                  >
                    <p className="text-white text-sm font-semibold">
                      {resaltar(item.titulo)}
                    </p>
                    <p className="text-gray-400 text-xs mt-0.5">
                      {item.descripcion}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="px-4 py-6 text-center">
              <p className="text-gray-400 text-sm">
                Sin resultados para{" "}
                <span className="text-white font-semibold">"{query}"</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}